import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Switch,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { campaignModel } from "../../Interfaces";
import {
  useDeleteCampaignMutation,
  useGetCampaignsQuery,
  useUpdateCampaignStatusMutation,
} from "../../Apis/campaignApi";
import { useDispatch } from "react-redux";
import { setCampaign } from "../../Storage/Redux/campaignSlice";
import { useNavigate } from "react-router-dom";

import { withAdminAuth } from "../../HOC";

export default withAdminAuth(function CampaignList() {
  const { data, isLoading, refetch } = useGetCampaignsQuery(null);
  const [updateCampaignStatus] = useUpdateCampaignStatusMutation();
  const [deleteCampaign] = useDeleteCampaignMutation();
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [localCampaigns, setLocalCampaigns] = useState<campaignModel[]>([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && data?.result) {
      dispatch(setCampaign(data.result));
      setLocalCampaigns(data.result);
    }
  }, [isLoading, data]);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleStatusChange = async (id: number, isActive: boolean) => {
    const original = [...localCampaigns];

    setUpdatingId(id);

    try {
      setLocalCampaigns((prev) =>
        prev.map((c) => (c.id === id ? { ...c, isActive } : c))
      );

      const response = await updateCampaignStatus({ id, isActive });

      if (response.data) {
        setSnackbar({
          open: true,
          message: `Campaign ${isActive ? "activated" : "deactivated"}`,
          severity: "success",
        });

        refetch();
      } else {
        setSnackbar({
          open: true,
          message: "Failed to update campaign",
          severity: "error",
        });

        setLocalCampaigns(original);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error updating campaign",
        severity: "error",
      });
      setLocalCampaigns(original);
    } finally {
      setUpdatingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!selectedId) return;

    try {
      await deleteCampaign({ id: selectedId }).unwrap();

      setSnackbar({
        open: true,
        message: "Campaign Deleted Successfully",
        severity: "success",
      });

      refetch();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.data?.errorMessages?.[0] || "Delete failed",
        severity: "error",
      });
    } finally {
      setOpenDialog(false);
      setSelectedId(null);
    }
  };

  return (
    <Card sx={{ p: 1 }}>
      <CardHeader
        title={
          <Typography variant="h5" sx={{ color: "#eb3c96", fontWeight: 600 }}>
            Campaign List
          </Typography>
        }
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/Campaign/create")}
            sx={{
              backgroundColor: "rgba(81, 35, 132, 1)",
              "&:hover": { backgroundColor: "rgba(81, 35, 132, 0.9)" },
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Add New Campaign
          </Button>
        }
        sx={{ pb: 0 }}
      />

      <CardContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : data?.result?.length ? (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.result.map((campaign: campaignModel, index: number) => (
                  <TableRow key={campaign.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell>
                      {new Date(campaign.startDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(campaign.endDate).toLocaleDateString()}
                    </TableCell>

                    {/* Status Switch */}
                    <TableCell>
                      {updatingId === campaign.id ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={18} />
                          <Typography variant="body2">Updating...</Typography>
                        </Box>
                      ) : (
                        <Switch
                          checked={campaign.isActive}
                          onChange={(e) =>
                            handleStatusChange(campaign.id, e.target.checked)
                          }
                        />
                      )}
                    </TableCell>

                    {/* Action Buttons */}
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() =>
                          navigate(`/Campaign/Details/${campaign.id}`)
                        }
                      >
                        <VisibilityIcon />
                      </IconButton>

                      <IconButton
                        color="secondary"
                        onClick={() =>
                          navigate(`/Campaign/Edit/${campaign.id}`)
                        }
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        color="error"
                        onClick={() => {
                          setSelectedId(campaign.id);
                          setOpenDialog(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography
              variant="h6"
              color="error"
              sx={{ fontFamily: "monospace" }}
            >
              No Data Found
            </Typography>
          </Box>
        )}
      </CardContent>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this campaign?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">
            Cancel
          </Button>

          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});
